import React, { useEffect, useState } from 'react';
import { Bell, BellOff } from 'lucide-react';
import {
  disableAdminPush,
  enableAdminPush,
  getAdminPushStatus,
  isPushSupported,
} from '../../utils/adminWebPush';
import { fetchAlertPreferences, updateAlertPreferences } from '../../utils/adminAlerts';

export default function AdminPushOptIn() {
  const [status, setStatus] = useState({ supported: false, subscribed: false });
  const [pushAll, setPushAll] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const refresh = async () => {
    if (!isPushSupported()) {
      setStatus({ supported: false, subscribed: false });
      return;
    }
    const s = await getAdminPushStatus();
    setStatus({
      supported: s.supported,
      subscribed: s.subscribed && s.serverSubscribed,
    });
    try {
      const prefs = await fetchAlertPreferences();
      setPushAll(prefs.push_all_alerts !== false);
    } catch (_) {
      setPushAll(true);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const toggleSubscribe = async () => {
    setLoading(true);
    setMessage('');
    try {
      if (status.subscribed) {
        await disableAdminPush();
        setMessage('Browser notifications disabled.');
      } else {
        await enableAdminPush();
        setMessage('Browser notifications enabled — you will receive a push for every alert.');
      }
      await refresh();
    } catch (e) {
      setMessage(e.message || 'Could not update push settings.');
    } finally {
      setLoading(false);
    }
  };

  const togglePushAll = async () => {
    const next = !pushAll;
    setLoading(true);
    setMessage('');
    try {
      await updateAlertPreferences({
        push_all_alerts: next,
        min_push_severity: next ? 'info' : 'warning',
        channels: { in_app: true, push: true, email: false },
      });
      setPushAll(next);
      setMessage(next ? 'Push enabled for all alert types.' : 'Push limited to warning and critical only.');
    } catch (e) {
      setMessage(e.message || 'Could not save preference.');
    } finally {
      setLoading(false);
    }
  };

  if (!status.supported) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
        Push notifications are not supported in this browser.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            {status.subscribed ? (
              <Bell className="w-5 h-5 text-red-600" />
            ) : (
              <BellOff className="w-5 h-5 text-gray-400" />
            )}
            Browser push alerts
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Receive a push for every staff alert (top-ups, support, bookings, refunds, credit) when
            this tab is in the background. In-app bell always shows all alerts.
          </p>
          {message && <p className="text-sm text-red-600 mt-2">{message}</p>}
        </div>
        <button
          type="button"
          disabled={loading}
          onClick={toggleSubscribe}
          className={`shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
            status.subscribed
              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              : 'bg-red-600 text-white hover:bg-red-700'
          } disabled:opacity-50`}
        >
          {loading ? '…' : status.subscribed ? 'Disable push' : 'Enable push'}
        </button>
      </div>

      {status.subscribed && (
        <label className="flex items-center gap-3 cursor-pointer pt-2 border-t border-gray-100">
          <input
            type="checkbox"
            checked={pushAll}
            disabled={loading}
            onChange={togglePushAll}
            className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
          />
          <span className="text-sm text-gray-700">
            Push for <strong>all</strong> alerts (including informational). Uncheck to limit push
            to warning and critical only.
          </span>
        </label>
      )}
    </div>
  );
}
